import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Mic, MicOff, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useVoiceRecognition } from './hooks/useVoiceRecognition';
import { useChatAutocomplete, type UseChatAutocompleteOptions } from './hooks/useChatAutocomplete';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import type { AutocompleteItem } from './hooks/useChatAutocomplete';

interface InputControlsProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  /** Options for autocomplete (commands, groups, celebrationId) */
  autocompleteOptions?: UseChatAutocompleteOptions;
  /** Optional callback to open calendar date picker */
  onOpenCalendar?: () => void;
}

export const InputControls: React.FC<InputControlsProps> = ({
  onSendMessage,
  isLoading,
  autocompleteOptions,
  onOpenCalendar,
}) => {
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isListening, transcript, startListening, stopListening } = useVoiceRecognition();

  const {
    state: autocompleteState,
    handleInputChange: handleAutocompleteChange,
    moveSelection,
    getSelectedItem,
    applySelection,
    close: closeAutocomplete,
  } = useChatAutocomplete(autocompleteOptions);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const message = inputValue.trim() || transcript.trim();
    if (message && !isLoading) {
      onSendMessage(message);
      setInputValue('');
      closeAutocomplete();
    }
  };

  const toggleVoice = async () => {
    if (isListening) {
      const finalTranscript = await stopListening();
      if (finalTranscript) {
        setInputValue(finalTranscript);
      }
    } else {
      startListening();
    }
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Notify autocomplete of the change
      const cursorPos = e.target.selectionStart ?? newValue.length;
      handleAutocompleteChange(newValue, cursorPos);
    },
    [handleAutocompleteChange],
  );

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      // Also update autocomplete on cursor movement (click, arrow in text)
      const target = e.target as HTMLTextAreaElement;
      handleAutocompleteChange(target.value, target.selectionStart ?? target.value.length);
    },
    [handleAutocompleteChange],
  );

  const handleAutocompleteSelect = useCallback(
    (item: AutocompleteItem) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart ?? inputValue.length;
      const { newText, newCursorPos } = applySelection(inputValue, cursorPos, item);

      setInputValue(newText);
      closeAutocomplete();

      // Restore focus and set cursor position
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    [inputValue, applySelection, closeAutocomplete],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // When autocomplete is open, intercept navigation keys
    if (autocompleteState.isOpen && autocompleteState.items.length > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelection('up');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelection('down');
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const selected = getSelectedItem();
        if (selected) {
          e.preventDefault();
          handleAutocompleteSelect(selected);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAutocomplete();
        return;
      }
    }

    // Default: Enter sends message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (!isLoading) {
      textareaRef.current?.focus();
    }
  }, [isLoading]);

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          {/* Autocomplete dropdown — positioned above input */}
          <AutocompleteDropdown
            state={autocompleteState}
            onSelect={handleAutocompleteSelect}
            onClose={closeAutocomplete}
          />

          <Textarea
            ref={textareaRef}
            value={inputValue || transcript}
            onChange={handleChange}
            onSelect={handleSelect}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? "Listening..." : "Type / for commands, @ for people, # for groups…"}
            className="resize-none pr-18"
            rows={1}
            autoFocus
          />
          {onOpenCalendar && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute right-9 top-1 h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
              onClick={onOpenCalendar}
              disabled={isLoading}
              title="Pick date on calendar"
              aria-label="Pick date on calendar"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-1 top-1 h-8 w-8"
            onClick={toggleVoice}
            disabled={isLoading}
            title={isListening ? "Stop listening" : "Voice input"}
            aria-label={isListening ? "Stop listening" : "Voice input"}
          >
            {isListening ? (
              <MicOff className="h-4 w-4 text-red-500" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
        </div>
        <Button
          type="submit"
          size="icon"
          disabled={isLoading || (!inputValue.trim() && !transcript.trim())}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};
